/* eslint-disable camelcase */
import { Stack } from 'component-library';

import {
  Recommendation,
  RecommendationCardEnum,
  RecommendationCardType,
} from '@/api/rest/resources/types/recommendations';
import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { parseTextOrI18nPath } from '@/components/CoPilot/utils/parseRecommendation';
import { getStylesByCardType } from '@/components/widgets/a/Card/utils/getStylesByCardType';
import { PillWidget } from '@/components/widgets/a/Pill/Pill';
import { Body, Footer, Header } from '@/components/widgets/a/Text/Text';
import { CardProps } from '@/components/widgets/o/Card/Card';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useRecommendations } from '@/hooks/useRecommendations';

export const CoPilotCards = () => {
  const recommendations = useRecommendations().data?.recommendations ?? [];

  const widgetSlotsConfiguration = {
    name: DefaultWidgetNames.default_template_copilot,
    type: WidgetType.t_page,
    widgets: recommendations.map((recommendation) => ({
      name: DefaultWidgetNames.default_recommendation_card,
      type: WidgetType.o_card,
      relatedData: parseRecommendationAsCoPilotCard(recommendation),
      visible: true,
    })),
  };

  return <WidgetSlotRenderer nestedWidget={widgetSlotsConfiguration} />;
};

const parseRecommendationAsCoPilotCard = (recommendation: Recommendation): CardProps => {
  const { recommendation_data } = recommendation;

  const { body, pill, header, footer_text, footer_value, body_expanded, recommendation_type } = recommendation_data;

  const recommendationType = recommendation_type as RecommendationCardType;

  if (!recommendationType || !RecommendationCardEnum[`${recommendationType}`]) {
    throw new Error(`Invalid recommendation type: ${recommendation_type}`);
  }

  const cardStyles = getStylesByCardType(recommendationType);
  const hasDetailsSection = !!body_expanded?.body;

  return {
    direction: 'row',
    styles: {
      root: 'p-4',
      header: `border-b-transparent ${hasDetailsSection ? 'border-r w-1/2' : ''}`,
      body: 'w-1/2',
    },
    centerMain: true,
    header: (
      <Stack spacing={6}>
        <PillWidget
          styles={cardStyles}
          {...parseTextOrI18nPath(pill, recommendationType, 'global.widget.recommendationCard.type')}
        />
        {header && <Header text={header} />}
        {body && <Body text={body} />}
        {(footer_text || footer_value) && (
          <Footer>
            {footer_text && <span className='font-semibold'>{footer_text}</span>}
            {footer_value && (
              <span className={cardStyles.color ? `text-[${cardStyles.color}]` : 'text-black-100'}>{footer_value}</span>
            )}
          </Footer>
        )}
      </Stack>
    ),
    body: hasDetailsSection ? (
      <Stack spacing={6}>
        <Header i18nPath='global.widget.recommendationCard.details' className='font-semibold typography-overline' />
        <Body text={body_expanded.body} />
      </Stack>
    ) : null,
  };
};
